import { ProductCard as AutopartsProductCard } from "@qubit/autoparts";
import type { ComponentProps, ReactNode } from "react";

import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { parseProductTempZone } from "~/lib/tempZone";

import { TemperatureZoneRibbon } from "./TemperatureZoneRibbon";

export type ProductCardRootProps = ComponentProps<
  typeof AutopartsProductCard.Root
> & {
  tempZone?: string | null;
};

type ProductCardContainerProps = ComponentProps<
  typeof AutopartsProductCard.ProductCardContainer
>;

/**
 * Re-exports autoparts `ProductCard` with a Qubit-specific `Root` wrapper.
 *
 * This wrapper exists so that each consumer of ProductCard doesn't need to call
 * the `useGetMultiTempGridInfo` hook and manage passing the ribbon prop.
 */
function ProductCardRootWithSideRibbon({
  tempZone,
  ...rest
}: ProductCardRootProps) {
  const { isMultiTempGrid } = useGetMultiTempGridInfo();
  const parsedTempZone = parseProductTempZone(tempZone);

  let sideRibbon: ReactNode | undefined;
  if (isMultiTempGrid && parsedTempZone !== null) {
    sideRibbon = <TemperatureZoneRibbon tempZone={parsedTempZone} />;
  }

  return (
    <ASDSThemeProvider>
      <AutopartsProductCard.Root {...rest} sideRibbon={sideRibbon} />
    </ASDSThemeProvider>
  );
}

function ProductCardContainerWithASDSThemeProvider(
  props: ProductCardContainerProps
) {
  return (
    <ASDSThemeProvider>
      <AutopartsProductCard.ProductCardContainer {...props} />
    </ASDSThemeProvider>
  );
}

// The rest of ProductCard remains in autoparts but is re-exported here for convenience.
export const ProductCard = {
  ...AutopartsProductCard,
  Root: ProductCardRootWithSideRibbon,
  ProductCardContainer: ProductCardContainerWithASDSThemeProvider
};
